// frontend/src/hooks/useInventoryData.js
// Reusable hooks for inventory reporting data. Pure logic — no JSX.

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { sessionApi } from '../services/SessionApiService';
import { apiErrorFrom } from '../utils/parseApiError';
import { useAuth } from '../contexts/AuthContext';
import { kpi as kpiKey, timelines as timelinesKey } from '../queries/keys';
import { useTimeSeriesFetcher } from './useTimeSeriesFetcher';
import { scopeParams } from '../features/item-filter/scopeParams';
import { readResult } from '../queries/api';
import { useTabPollInterval } from '../tabs/tabsContexts';

const THIRTY_SEC = 30 * 1000;
const SIXTY_SEC = 60 * 1000;

/**
 * Fetch inventory KPI data: latest value, change since last month, daily values.
 * Used by the KPI strip Inventory tile.
 *
 * Caching:
 *   staleTime 30s — KPIs are glanceable; merchant expects "fresh enough"
 *   refetchInterval 60s — polls while the page's tab is on screen
 *     (`useTabPollInterval`: a hidden tab stays mounted and would otherwise
 *     keep polling; TanStack itself pauses polling while the browser window
 *     is hidden)
 *   refetchOnWindowFocus inherited (true) — re-read when the browser window
 *     comes back
 *
 * Returns {data, loading, error, refetch}; `error` is the translated string
 * consumers display via Alert.
 */
export function useInventoryKpi() {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const refetchInterval = useTabPollInterval(SIXTY_SEC);
  const query = useQuery({
    queryKey: kpiKey.inventory(storeId),
    queryFn: async () => {
      const res = await sessionApi.get('/api/reports/inventory/kpi/');
      if (!res.ok) throw await apiErrorFrom(res);
      return res.json();
    },
    staleTime: THIRTY_SEC,
    refetchInterval,
    enabled: !!storeId,
  });
  return readResult(query, t);
}

/**
 * Fetch unified inventory timeline (historical + projected available balance).
 * Uses live Inventory for current period + PO receipts + forecast demand.
 * Supports day/week/month granularity and historyMonths=0 for projection-only mode.
 *
 * `historyMonths` counts completed months before the current one; `months`
 * counts the current month plus the months after it. Each window param is
 * sent only when the caller sets it: the endpoint owns the defaults.
 */
export function useInventoryTimeline({ metric, granularity, historyMonths, months, scope }) {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const params = {
    metric,
    ...(granularity !== undefined && { granularity }),
    ...(historyMonths !== undefined && { history_months: String(historyMonths) }),
    ...(months !== undefined && { projection_months: String(months) }),
    ...scopeParams(scope),
  };

  return useTimeSeriesFetcher({
    queryKey: timelinesKey.inventory(storeId, params),
    endpoint: '/api/reports/inventory/timeline/',
    params,
    enabled: !!storeId,
  });
}
